<template>
  <div>
    <!-- Header Section -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between py-4 pb-2 px-2 gap-4">
      <div class="flex items-center">
        <TentTree class="w-5 h-5 mx-2 text-gray-600" />
        <h1 class="text-gray-900">Adventurers</h1>
      </div>

      <!-- Search Bar -->
      <div class="relative w-full lg:w-[300px]">
        <Search
          class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
        />
        <input
          type="text"
          placeholder="Search adventurers..."
          class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          v-model="searchQuery"
          @input="handleSearchInput"
        />
      </div>
    </div>

    <!-- Filters row -->
    <div class="flex items-center gap-2 flex-wrap px-4 pb-3">
      <!-- Program Status filter -->
      <Popover>
        <PopoverTrigger as-child>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border bg-white hover:bg-gray-50 transition-colors font-medium"
            :class="selectedProgramStatus
              ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
              : 'border-gray-200 text-gray-600'"
          >
            <Activity class="w-3 h-3" />
            Program Status
            <span
              v-if="selectedProgramStatus"
              class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
            >1</span>
            <ChevronDown class="w-3 h-3 opacity-40" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-56 p-0" align="start">
          <div class="max-h-52 overflow-y-auto p-1">
            <div
              @click="selectProgramStatus('')"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="!selectedProgramStatus ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="!selectedProgramStatus" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">All Program Statuses</span>
            </div>
            <div
              v-for="(value, key) in programStatusChoices"
              :key="key"
              @click="selectProgramStatus(String(key))"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="selectedProgramStatus === String(key) ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="selectedProgramStatus === String(key)" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">{{ value }}</span>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <!-- Relationship filter -->
      <Popover>
        <PopoverTrigger as-child>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border bg-white hover:bg-gray-50 transition-colors font-medium"
            :class="selectedRelationship
              ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
              : 'border-gray-200 text-gray-600'"
          >
            <Users class="w-3 h-3" />
            Relationship
            <span
              v-if="selectedRelationship"
              class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
            >1</span>
            <ChevronDown class="w-3 h-3 opacity-40" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-56 p-0" align="start">
          <div class="max-h-52 overflow-y-auto p-1">
            <div
              @click="selectRelationship('')"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="!selectedRelationship ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="!selectedRelationship" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">All Relationships</span>
            </div>
            <div
              v-for="(value, key) in relationshipChoices"
              :key="key"
              @click="selectRelationship(String(key))"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="selectedRelationship === String(key) ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="selectedRelationship === String(key)" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">{{ value }}</span>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <!-- Country filter -->
      <Popover>
        <PopoverTrigger as-child>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border bg-white hover:bg-gray-50 transition-colors font-medium"
            :class="selectedCountry !== 'all'
              ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
              : 'border-gray-200 text-gray-600'"
            :disabled="countryStore.isLoading"
          >
            <Globe class="w-3 h-3" />
            Country
            <span
              v-if="selectedCountry !== 'all'"
              class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
            >1</span>
            <ChevronDown class="w-3 h-3 opacity-40" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-56 p-0" align="start">
          <div class="p-2 border-b border-gray-100">
            <div class="relative">
              <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
              <input
                v-model="countrySearchTerm"
                placeholder="Search country..."
                class="w-full h-7 pl-7 pr-2 text-xs rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-cosmicPurple/30 focus:border-cosmicPurple/30"
              />
            </div>
          </div>
          <div class="max-h-52 overflow-y-auto p-1">
            <div
              @click="selectCountry('all')"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="selectedCountry === 'all' ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="selectedCountry === 'all'" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">All Countries</span>
            </div>
            <div
              v-for="country in filteredCountries"
              :key="country.value"
              @click="selectCountry(country.value)"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="selectedCountry === country.value ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="selectedCountry === country.value" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700 truncate">{{ country.label }}</span>
            </div>
            <div v-if="filteredCountries.length === 0" class="py-4 text-center text-xs text-gray-400">
              No countries found
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <!-- Batch filter -->
      <Popover>
        <PopoverTrigger as-child>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border bg-white hover:bg-gray-50 transition-colors font-medium"
            :class="selectedBatchId
              ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
              : 'border-gray-200 text-gray-600'"
          >
            <Layers class="w-3 h-3" />
            Batch
            <span
              v-if="selectedBatchId"
              class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
            >1</span>
            <ChevronDown class="w-3 h-3 opacity-40" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-64 p-0" align="start">
          <div class="max-h-52 overflow-y-auto p-1">
            <div
              @click="selectBatch('')"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="!selectedBatchId ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="!selectedBatchId" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700">All Batches</span>
            </div>
            <div
              v-for="batch in batchChoices"
              :key="batch.id"
              @click="selectBatch(batch.id)"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer select-none"
            >
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                :class="selectedBatchId === batch.id ? 'bg-cosmicPurple border-cosmicPurple' : 'border-gray-300'"
              >
                <Check v-if="selectedBatchId === batch.id" class="w-2.5 h-2.5 text-white" />
              </div>
              <span class="text-xs text-gray-700 truncate">{{ batch.name }}</span>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <!-- Clear all -->
      <button
        v-if="isAnyFilterApplied"
        @click="resetFilters"
        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-500 transition-colors"
      >
        <X class="w-3 h-3" />
        Clear all
      </button>
    </div>

    <!-- Active filter chips -->
    <div
      v-if="activeFilterChips.length > 0"
      class="flex flex-wrap gap-1.5 px-4 pb-3"
    >
      <span
        v-for="chip in activeFilterChips"
        :key="chip.key"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs rounded-full bg-cosmicPurple/10 text-cosmicPurple border border-cosmicPurple/20"
      >
        {{ chip.label }}
        <button
          @click="chip.remove()"
          class="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-cosmicPurple/20 transition-colors"
        >
          <X class="w-2.5 h-2.5" />
        </button>
      </span>
    </div>

    <!-- Content Section -->
    <div class="py-4 px-2 pt-2">
      <div v-if="isLoadingAdventurers" class="flex items-center justify-center py-8">
        <div class="text-center">
          <Loader2 class="animate-spin h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p class="text-gray-500">Loading adventurers...</p>
        </div>
      </div>
      <div v-else-if="tableAdventurers.length > 0" class="flex flex-col gap-4 overflow-x-auto">
        <BatchTable
          :batch-name="tableTitle"
          :adventurers="tableAdventurers"
          :show-batch-column="showBatchColumn"
        />
        <div
          v-if="isViewingAllFellows && hasMoreAllFellows"
          class="flex flex-col items-center justify-center py-4 w-full"
        >
          <Loader2
            v-if="isLoadingMore"
            class="animate-spin h-6 w-6 text-gray-400"
          />
          <div ref="allFellowsSentinel" class="h-4 w-full" />
        </div>
      </div>
      <div v-else class="flex flex-col items-center justify-center py-12 text-center px-4">
        <SearchX class="w-12 h-12 text-gray-400 mb-3" />
        <h3 class="text-lg font-medium text-gray-700 mb-1">No adventurers found</h3>
        <p class="text-gray-500 mb-4">Try adjusting your filters or search.</p>
        <button
          @click="resetFilters"
          class="text-sm text-cosmicPurple hover:text-cosmicPurple/80 flex items-center gap-1.5"
        >
          <RotateCcw class="w-4 h-4" />
          Reset filters
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { Loader2, SearchX, RotateCcw, Search, TentTree, ChevronDown, Check, X, Activity, Users, Globe, Layers } from 'lucide-vue-next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import BatchTable from '@/components/manage/BatchTable.vue'
import { useAdventurersStore } from '@/stores/adventurers'
import { useCountryStore } from '@/stores/countries'
import { useDebounce } from '@/composables/useDebounce'
import { programStatusChoices, relationshipChoices } from '@/const/fellowChoices'

const adventurersStore = useAdventurersStore()
const countryStore = useCountryStore()
const { debounce } = useDebounce(300)

const searchQuery = ref('')
const selectedCountry = ref<number | 'all'>('all')
const selectedProgramStatus = ref('')
const selectedRelationship = ref('')
const selectedBatchId = ref('')
const countrySearchTerm = ref('')

const batchChoices = computed(() => adventurersStore.batchChoices)
const isLoadingAdventurers = computed(() => adventurersStore.isLoading)
const allFellows = computed(() => adventurersStore.allFellows)
const hasMoreAllFellows = computed(() => !!adventurersStore.allFellowsNextUrl)
const isLoadingMore = computed(() => adventurersStore.isLoadingMore)
const selectedBatchData = computed(() => adventurersStore.selectedBatchData || [])
const allFellowsSentinel = ref<HTMLElement | null>(null)
let allFellowsObserver: IntersectionObserver | null = null

const tableAdventurers = computed(() =>
  selectedBatchId.value ? selectedBatchData.value : allFellows.value
)
const tableTitle = computed(() =>
  selectedBatchId.value ? formatBatchName(selectedBatchId.value) : 'All Fellows'
)
const showBatchColumn = computed(() => !selectedBatchId.value)
const isViewingAllFellows = computed(() => !selectedBatchId.value)

const isAnyFilterApplied = computed(() => {
  return selectedProgramStatus.value !== '' ||
    selectedRelationship.value !== '' ||
    selectedCountry.value !== 'all' ||
    selectedBatchId.value !== ''
})

const filteredCountries = computed(() => {
  if (!countrySearchTerm.value) return countryStore.fellowCountries
  return countryStore.fellowCountries.filter((c: { label: string }) =>
    c.label.toLowerCase().includes(countrySearchTerm.value.toLowerCase())
  )
})

const activeFilterChips = computed(() => {
  const chips: { key: string; label: string; remove: () => void }[] = []

  if (selectedProgramStatus.value) {
    const label = programStatusChoices[selectedProgramStatus.value as keyof typeof programStatusChoices] || selectedProgramStatus.value
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    chips.push({ key: 'programStatus', label: `Status: ${label}`, remove: () => selectProgramStatus('') })
  }
  if (selectedRelationship.value) {
    const label = relationshipChoices[selectedRelationship.value as keyof typeof relationshipChoices] || selectedRelationship.value
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    chips.push({ key: 'relationship', label: `Relationship: ${label}`, remove: () => selectRelationship('') })
  }
  if (selectedCountry.value !== 'all') {
    const country = countryStore.fellowCountries.find((c: { value: number }) => c.value === selectedCountry.value)
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    chips.push({ key: 'country', label: `Country: ${country?.label || selectedCountry.value}`, remove: () => selectCountry('all') })
  }
  if (selectedBatchId.value) {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    chips.push({ key: 'batch', label: `Batch: ${formatBatchName(selectedBatchId.value)}`, remove: () => selectBatch('') })
  }

  return chips
})

const fetchAdventurers = async (options: { includeRelationship?: boolean } = {}) => {
  if (selectedBatchId.value) {
    await adventurersStore.fetchAdventurersByBatch(selectedBatchId.value)
    return
  }

  const includeRelationship = options.includeRelationship ?? !searchQuery.value.trim()

  await adventurersStore.fetchAllAdventurers({
    outtalent_program_status: selectedProgramStatus.value || undefined,
    outtalent_relationship: includeRelationship ? selectedRelationship.value || undefined : undefined,
    country: selectedCountry.value === 'all' ? undefined : selectedCountry.value,
    q: searchQuery.value.trim() || undefined
  })
}

const debouncedFetchSearch = debounce(() => {
  const hasSearchQuery = !!searchQuery.value.trim()
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchAdventurers({ includeRelationship: !hasSearchQuery })
})

const handleSearchInput = () => {
  if (selectedBatchId.value) {
    selectedBatchId.value = ''
  }
  debouncedFetchSearch()
}

const selectProgramStatus = async (value: string) => {
  selectedProgramStatus.value = value
  if (selectedBatchId.value) selectedBatchId.value = ''
  await fetchAdventurers()
}

const selectRelationship = async (value: string) => {
  selectedRelationship.value = value
  if (selectedBatchId.value) selectedBatchId.value = ''
  await fetchAdventurers()
}

const selectCountry = async (value: number | 'all') => {
  selectedCountry.value = value
  if (selectedBatchId.value) selectedBatchId.value = ''
  await fetchAdventurers()
}

const selectBatch = async (value: string) => {
  selectedBatchId.value = value
  if (value) searchQuery.value = ''
  await fetchAdventurers()
}

const resetFilters = async () => {
  searchQuery.value = ''
  selectedCountry.value = 'all'
  selectedProgramStatus.value = ''
  selectedRelationship.value = ''
  selectedBatchId.value = ''
  countrySearchTerm.value = ''
  await fetchAdventurers()
}

const formatBatchName = (batchId: string) => {
  return batchId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

onMounted(async () => {
  await Promise.all([adventurersStore.fetchBatchChoices(), countryStore.fetchFellowCountries()])
  await fetchAdventurers()
})

const setupAllFellowsObserver = () => {
  if (!allFellowsSentinel.value) return
  if (allFellowsObserver) {
    allFellowsObserver.disconnect()
  }

  allFellowsObserver = new IntersectionObserver(
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async (entries) => {
      if (!entries[0]?.isIntersecting) return
      if (!isViewingAllFellows.value || !hasMoreAllFellows.value) return
      await adventurersStore.fetchMoreFellows()
    },
    { rootMargin: '200px 0px', threshold: 0.1 }
  )

  allFellowsObserver.observe(allFellowsSentinel.value)
}

watch(
  [allFellowsSentinel, isViewingAllFellows, hasMoreAllFellows],
  ([sentinel, viewing, hasMore]) => {
    if (sentinel && viewing && hasMore) {
      setupAllFellowsObserver()
    } else if (allFellowsObserver) {
      allFellowsObserver.disconnect()
    }
  }
)

onBeforeUnmount(() => {
  if (allFellowsObserver) {
    allFellowsObserver.disconnect()
  }
})
</script>
